import React, { useState, useMemo, useEffect } from "react";
import { withTranslation } from "react-i18next";
import {
  Button,
  Checkbox,
  Grid,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";
import moment from "moment";

import SelectField from "Components/Common/SelectField";
import BreadCrumb from "Components/Common/BreadCrumb";
import {
  dateFormats,
  dcsApplicationsRoles,
  powerApplicationsRoles,
  SUPER_ADMIN,
} from "common/constant";
import {
  getOptionLabel,
  reportOptions,
  getOptionValue,
  reportTypeEnum,
  reportOptionsForDcs,
  monthEnum,
} from "common/enums";
import BaseTable from "Components/Common/BaseTable";
import Backdrop from "Components/Common/Backdrop";
import DatePicker from "Components/Common/DatePicker";
import ActiveDisconnectCategoryOrTariffTable from "./ActiveDisconnectCategoryOrTariffTable";
import ComplaintTable from "./ComplaintTable";
import GERCTable from "./GERCTable";
import ConsumerAndMeteringStatusTable from "./ConsumerAndMeteringStatusTable";
import MonthlyApplicationCountTable from "./MonthlyApplicationCountTable";
import MaximumDemandConsumerWiseTable from "./MaximumDemandConsumerWiseTable";
import PowerAbstractTable from "pages/Admin/PowerAbstract/PowerAbstractTable";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { convertCamelTextToCapitalizeText } from "utils/convertCamelTextToCapitalizeText";
import { dateFormatter } from "utils/dateFormatter";
import { isAuthorizedUser } from "utils";

import { useLazyGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyDownloadExcelQuery, useLazyGetQuery } from "slices/api";
import { useLazyGetOtherUtilityConsumerNumberOptionsByUtilityId } from "apis/UtilityConsumer/utilityConsumer";
import { generateYearOptions } from "utils/generateYearOptions";

const BY_DEFAULT_VALUE = 1;
const BY_DEFAULT_DCS_VALUE = 27;
const RECORDS_PER_PAGE = 20;

const applyColumnPropsOnKey = "connectionAddress";

const consumerMasterDataColumns = [
  "virtualId",
  "consumerNumber",
  "consumerName",
  "email",
  "mobileNumber",
  "connectionAddress",
  "locationOfService",
  "tariff",
  "category",
  "supplyVoltage",
  "typeOfMetering",
  "contractDemand",
  "unit",
  "meterInstallationDate",
  "meterSerialNumber",
  "mf",
  "electricityDuty",
  "applicationsStatus",
];

const consumerC4DataColumns = [
  "virtualId",
  "block",
  "building",
  "category",
  "tariff",
  "connectionAddress",
  "consumerName",
  "consumerNumber",
  "unit",
  "dateOfApplication",
  "dateOfConnection",
  "dateOfDisconnection",
];

const consumerVirtualAccountColumns = [
  { title: "Company LU CD1", field: "companyLUCD1" },
  { title: "APAC", field: "apac" },
  { title: "Dealer CD", field: "dealerCD" },
  { title: "Dealer Name", field: "dealerName" },
  { title: "Dr Narration", field: "drNarration" },
  { title: "Cr Narration", field: "crNarration" },
  { title: "Email Id", field: "emailId" },
  { title: "Mobile", field: "mobile" },
  { title: "Other Info1", field: "otherInfo1" },
  { title: "Other Info2", field: "otherInfo2" },
  { title: "Remarks", field: "remarks" },
  { title: "Map Virtual APAC", field: "mapVirtualAPAC" },
  { title: "Remitter APAC Check Required", field: "remitterAPACCheckRequired" },
  { title: "Partial Check Flag", field: "partialCheckFlag" },
  { title: "IMPS Enabled Flag", field: "impsEnabledFlag" },
];

const getTableColumns = (
  columns,
  columnData = {},
  fieldKey,
  valueKey,
  parentKey = ""
) => {
  Object.keys(columnData).forEach((key) => {
    if (columnData[key] && typeof columnData[key] === "object") {
      getTableColumns(
        columns,
        columnData[key],
        fieldKey,
        valueKey,
        `${parentKey ? `${parentKey}.` : ""}${key}`
      );
    } else {
      columns.push({
        [fieldKey]: convertCamelTextToCapitalizeText(key),
        [valueKey]: `${parentKey ? `${parentKey}.` : ""}${key}`,
        columnProps:
          applyColumnPropsOnKey === key ? { style: { minWidth: "350px" } } : {},
      });
    }
  });
};

const Reports = ({ t }) => {
  const [rowTableData, setRowTableData] = useState([]);
  const [billCycleId, setBillCycleId] = useState("");
  const [consumerPayload, setConsumerPayload] = useState({
    consumerIds: [],
    years: [],
  });
  const [serviceLineMonth, setServiceLineMonth] = useState(null);

  const [query, setQuery] = useState({
    year: moment(),
    dateTime: moment(),
    reportType: getOptionValue("reportTypeEnum", "New Connection"),
  });
  const sm = useMediaQuery("(max-width: 599px)");

  const [
    downloadExcel,
    { isLoading: isExcelFileDownloading, isFetching: isDownloadExcelFetching },
  ] = useLazyDownloadExcelQuery();

  const [
    getConsumerNumber,
    {
      data: { data: consumerNumberOptions } = {},
      isLoading: isConsumerLoading,
    },
  ] = useLazyGetOtherUtilityConsumerNumberOptionsByUtilityId();

  const [
    getTableData,
    { data: { data: tableData } = {}, isLoading, isFetching },
  ] = useLazyGetQuery();
  const [
    fetchBillingOptions,
    {
      data: { data: billingCycleOptions } = {},
      isLoading: isBillingCycleOptionsLoading,
    },
  ] = useLazyGetBillingCycleOptionsQuery();

  const { roles } = useSelector((state) => state.Login);

  const { isSuperAdmin, isDcsAdmin, isPowerAndOtherAdmin } = useMemo(
    () => ({
      isDcsAdmin: isAuthorizedUser(dcsApplicationsRoles, roles),
      isSuperAdmin: roles.includes(SUPER_ADMIN),
      isPowerAndOtherAdmin:
        isAuthorizedUser(powerApplicationsRoles, roles) &&
        isAuthorizedUser(dcsApplicationsRoles, roles),
    }),
    [roles]
  );

  const [selectedReportId, setSelectedReportId] = useState(
    isPowerAndOtherAdmin
      ? BY_DEFAULT_VALUE
      : isDcsAdmin
      ? BY_DEFAULT_DCS_VALUE
      : BY_DEFAULT_VALUE
  );

  const reportsData = useMemo(
    () => [
      {
        value: 1,
        endpoint: "/report/application-status-history",
        downloadSheetEndpoint: "/report/all-application-status-history-excel",
        sheetName: "Application Status History-All",
      },
      {
        value: 2,
        endpoint: "/power/application/report/list-of-application",
        downloadSheetEndpoint:
          "/power/application/report/list-of-application-excel",
        sheetName: "List of Applications",
      },
      {
        value: 3,
        endpoint: "/power/application/report/monthly-application-count",
        downloadSheetEndpoint:
          "/power/application/report/monthly-application-count-excel",
        Component: MonthlyApplicationCountTable,
        sheetName: "Monthly Application Count",
      },
      {
        value: 4,
        endpoint: "/report/consumer",
        downloadSheetEndpoint: "/report/consumer-master-data-excel",
        sheetName: "Consumer Master Data",
      },
      {
        value: 5,
        endpoint: "/report/consumer",
        downloadSheetEndpoint: "/report/consumer-data-c4-excel",
        sheetName: "Consumer Data-C4",
      },
      {
        value: 6,
        endpoint: "/report/consumer-activated-disconnected-tariff-wise",
        downloadSheetEndpoint:
          "/report/consumer-activated-disconnected-tariff-wise-excel",
        sheetName: "Active and Disconnected Consumers",
        isQueryRequired: true,
        Component: ActiveDisconnectCategoryOrTariffTable,
        componentProps: {
          keys: [
            "activatedAndDisconnectedMonthWise",
            "utilityConsumerTariffActivatedAndDisconnected",
            "tariffName",
            "activated",
            "disconnected",
          ],
        },
      },
      {
        value: 7,
        endpoint: "/report/consumer-activated-disconnected-category-wise",
        downloadSheetEndpoint:
          "/report/consumer-activated-disconnected-category-wise-excel",
        sheetName: "Active and Disconnected Category Wise",
        isQueryRequired: true,
        Component: ActiveDisconnectCategoryOrTariffTable,
        componentProps: {
          keys: [
            "activatedAndDisconnectedMonthWise",
            "utilityConsumerCategoryActivatedAndDisconnected",
            "category",
            "activated",
            "disconnected",
          ],
        },
      },
      {
        value: 8,
        endpoint: "/report/contact-detail-report",
        downloadSheetEndpoint: "/report/contact-details-excel",
        sheetName: "Contact Details",
      },
      {
        value: 9,
        endpoint: "/report/backup-power",
        downloadSheetEndpoint: "/report/backup-power-details-excel",
        sheetName: "Backup Power Details",
      },
      {
        value: 10,
        endpoint: "/report/net-metering",
        downloadSheetEndpoint: "/report/net-metering-excel",
        sheetName: "Net Metering",
      },
      {
        value: 11,
        endpoint: "/report/consumer-complaint",
        sheetName: "Number of Complaints",
        downloadSheetEndpoint: "/report/consumer-complaint-excel",
        isQueryRequired: true,
        Component: ComplaintTable,
      },
      {
        value: 12,
        endpoint: "/report/sop-gerc",
        sheetName: "GERC",
        isQueryRequired: true,
        downloadSheetEndpoint: "/report/sop-gerc-excel",
        Component: GERCTable,
      },
      {
        value: 13,
        endpoint: "/report/maximum-demand-consumer-wise",
        sheetName: "Maximum Demand Consumer Wise",
        isQueryRequired: true,
        downloadSheetEndpoint: "/report/maximum-demand-consumer-wise-excel",
        Component: MaximumDemandConsumerWiseTable,
      },
      // {
      //   value: 14,
      //   endpoint: "",
      //   sheetName: "Maximum Demand-TR Wise",
      //   isQueryRequired: true,
      // },
      {
        value: 15,
        endpoint: "/report/connection-load-category-wise",
        downloadSheetEndpoint: "/report/connection-load-category-wise-excel",
        sheetName: "Category Wise Connected Load",
      },
      {
        value: 16,
        endpoint: "/report/asset-detail",
        downloadSheetEndpoint: "/report/asset-detail-excel",
        sheetName: "Asset Details",
      },
      {
        value: 17,
        endpoint: "/report/metering-status",
        downloadSheetEndpoint: "/report/metering-status-excel",
        sheetName: "Metering Status",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "meterTypeCountMonthWise",
            "meterTypeCount",
            "meterName",
            "count",
          ],
        },
      },
      {
        value: 18,
        endpoint: "/report/sim-detail",
        downloadSheetEndpoint: "/report/sim-detail-excel",
        sheetName: "SIM Details",
      },
      {
        value: 19,
        endpoint: "/report/consumer-category-wise",
        downloadSheetEndpoint: "/report/consumer-category-wise-excel",
        sheetName: "Consumer Category Wise",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: [
            "utilityConsumerMonthWise",
            "utilityConsumerCategoryCount",
            "category",
            "count",
          ],
        },
      },
      {
        value: 20,
        endpoint: "/report/consumer-tariff-wise",
        sheetName: "consumer Tariff Wise",
        downloadSheetEndpoint: "/report/consumer-tariff-wise-excel",
        isQueryRequired: true,
        Component: ConsumerAndMeteringStatusTable,
        componentProps: {
          keys: ["tariffRecords", "tariffDetails", "tariffName", "count"],
        },
      },
      {
        value: 21,
        endpoint: `/report/billing-cycle/${billCycleId}/bill-calculation`,
        sheetName: "Billing Calculation",
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/bill-calculation-excel`,
      },
      {
        value: 22,
        endpoint: "/report/dispatched-bill-report",
        isQueryDateTime: true,
        sheetName: "Dispatched Bills",
        downloadSheetEndpoint: "/report/dispatched-bill-report-excel",
      },
      {
        value: 23,
        endpoint: `/report/billing-cycle/${billCycleId}/electricity-bill-abstract-report`,
        sheetName: "Power Abstract",
        isServerSidePaginationRequired: true,
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/electricity-bill-abstract-report-excel`,
        Component: PowerAbstractTable,
        componentProps: {
          powerAbstractTableProps: { billingCycleId: billCycleId },
        },
      },
      {
        value: 24,
        endpoint: `/report/billing-cycle/${billCycleId}/invoice-uploader-report`,
        sheetName: "Invoice Uploader",
        downloadSheetEndpoint: `/report/billing-cycle/${billCycleId}/invoice-uploader-report-excel`,
      },
      {
        value: 25,
        endpoint: "/report/consumer-virtual-account-report",
        sheetName: getOptionLabel("reportTypeEnum", query.reportType),
        isQueryDateTime: true,
        downloadSheetEndpoint: "/report/consumer-virtual-account-report-excel",
      },
      {
        value: 26,
        endpoint: "",
        sheetName: "Monthly Application Summary (MIS)",
        isQueryDateTime: true,
        downloadSheetEndpoint:
          "/power/application/report/monthly-application-summary-excel",
      },
      {
        value: 27,
        endpoint: "",
        sheetName: `Sales order Connection Charges Security Deposit ${query.dateTime?.format(
          "MMM YY"
        )}`,
        isQueryDateTime: true,
        downloadSheetEndpoint: "/report/dcs-sap-salesorder-charge-excel",
      },
      {
        value: 28,
        endpoint: "",
        sheetName: "DCS Consumer Master Data Report",
        downloadSheetEndpoint:
          "/report/dcs-utility-consumer-master-data-report-excel",
      },
      {
        value: 29,
        endpoint: "",
        sheetName: "DCS Name Change Details Report",
        downloadSheetEndpoint: "/report/dcs-name-change-details-report-excel",
      },
      {
        value: 30,
        endpoint: "",
        sheetName: "Backup Power Invoice Report",
        downloadSheetEndpoint: `/backup-power-bill-calculation/${billCycleId}/backup-power-report-excel`,
      },
      {
        value: 31,
        endpoint: "",
        sheetName: "Backup Power Abstract Report",
        downloadSheetEndpoint: `/backup-power-bill-calculation/${billCycleId}/backup-power-abstract-excel`,
      },
      {
        value: 32,
        endpoint: "",
        sheetName: "Consumer Wise Bill Report",
        isConsumerWiseBillReport: true,
        downloadSheetEndpoint:
          "/report/dcs-utility-consumer-year-wise-bill-data-excel",
      },
      {
        value: 33,
        endpoint: "",
        sheetName: `Service Line Contribution Bifurcation ${
          query.year
            ? query.year?.format("YYYY") +
              "-" +
              moment(query.year).add(1, "year").format("YY")
            : ""
        }`,
        isServiceLine: true,
        isQueryRequired: true,
        downloadSheetEndpoint: "/report/consumer-bifurcation-report-excel",
      },
    ],
    [billCycleId, query.reportType, query.dateTime,query.year]
  );

  const {
    endpoint,
    isQueryRequired,
    isQueryDateTime,
    isConsumerWiseBillReport,
    Component,
    downloadSheetEndpoint,
    isServerSidePaginationRequired,
    sheetName,
    componentProps,
    isServiceLine,
  } = useMemo(
    () => reportsData.find(({ value }) => value === selectedReportId) ?? {},
    [selectedReportId, reportsData]
  );

  const updatedReportOptions = useMemo(
    () =>
      isPowerAndOtherAdmin
        ? [...reportOptions, ...reportOptionsForDcs]
        : isDcsAdmin
        ? reportOptionsForDcs
        : reportOptions,
    [isDcsAdmin, isSuperAdmin]
  );

  const isConsumerVirtualAccountReportSelected = useMemo(
    () =>
      getOptionLabel("reportOptions", selectedReportId) ===
      "Consumer Virtual Account",
    [selectedReportId]
  );

  const columns = useMemo(() => {
    let tableColumns = [];
    if (
      selectedReportId ===
      getOptionValue("reportOptions", "Consumer Master Data")
    ) {
      consumerMasterDataColumns.map((item) =>
        tableColumns.push({
          title: convertCamelTextToCapitalizeText(item),
          field: item,
          columnProps:
            applyColumnPropsOnKey === item
              ? { style: { minWidth: "350px" } }
              : {},
        })
      );
    } else if (
      selectedReportId === getOptionValue("reportOptions", "Consumer Data")
    ) {
      consumerC4DataColumns.map((item) =>
        tableColumns.push({
          title: convertCamelTextToCapitalizeText(item),
          field: item,
          columnProps:
            applyColumnPropsOnKey === item
              ? { style: { minWidth: "350px" } }
              : {},
        })
      );
    } else if (isConsumerVirtualAccountReportSelected) {
      tableColumns = consumerVirtualAccountColumns;
    } else {
      getTableColumns(tableColumns, tableData?.[0], "title", "field");
    }
    return tableColumns;
  }, [tableData, selectedReportId]);

  const getReportData = async () => {
    try {
      if (
        (getOptionValue("reportOptions", "Billing Calculation") !==
          selectedReportId &&
          getOptionValue("reportOptions", "Invoice Uploader") !==
            selectedReportId) ||
        ((getOptionValue("reportOptions", "Billing Calculation") ===
          selectedReportId ||
          getOptionValue("reportOptions", "Invoice Uploader") ===
            selectedReportId) &&
          billCycleId)
      ) {
        const { data: { data: tableData } = {} } = await getTableData(
          {
            endpoint,
            query: isQueryRequired
              ? { year: query.year.format("YYYY") }
              : isQueryDateTime
              ? {
                  dateTime: query.dateTime.format("YYYY-MM-DD"),
                  ...(isConsumerVirtualAccountReportSelected && {
                    reportType: query.reportType,
                  }),
                }
              : {},
          },
          true
        );
        if (tableData) {
          const data = Array.isArray(tableData) ? tableData : [tableData];

          const updatedTableData = data.map((row) =>
            Object.entries(row).reduce((res, [key, value]) => {
              if (
                typeof value === "string" &&
                moment(value, dateFormats, true).isValid()
              ) {
                return {
                  ...res,
                  [key]: convertUTCDateToLocalDate(value).format("DD-MM-YYYY"),
                };
              }
              return { ...res, [key]: value };
            }, {})
          );
          setRowTableData(updatedTableData || []);
        }
      }
    } catch (error) {}
  };

  const getReportUrl = async () => {
    if (downloadSheetEndpoint) {
      const payload = isConsumerWiseBillReport
        ? {
            consumerIds: consumerPayload.consumerIds.map(({ value }) => value),
            years: consumerPayload.years.map(({ value }) => value),
          }
        : {};
      try {
        await downloadExcel({
          endpoint: downloadSheetEndpoint,
          sheetName,
          query:isQueryRequired&&query.year&&serviceLineMonth?.value?{ year: query.year.format("YYYY"),month:serviceLineMonth?.value ?? null }: isQueryRequired&&query.year
            ? { year: query.year.format("YYYY") }
            : isQueryDateTime
            ? {
                dateTime: query.dateTime.format("YYYY-MM-DD"),
                ...(isConsumerVirtualAccountReportSelected && {
                  reportType: query.reportType,
                }),
              }
            : isConsumerWiseBillReport
            ? payload
            : {},
        });
      } catch (error) {}
    }
  };

  useEffect(() => {
    if (!isServerSidePaginationRequired && !!endpoint) {
      getReportData();
    }
  }, [
    endpoint,
    query?.year,
    query?.dateTime,
    query.reportType,
    sheetName,
    isServerSidePaginationRequired,
  ]);

  const billCycleValue = useMemo(
    () => billingCycleOptions?.find(({ id }) => id === billCycleId),
    [billingCycleOptions, billCycleId]
  );

  const updatedBillingCycleOptions = useMemo(() => {
    if (!billingCycleOptions?.length) return [];

    const targetSubscriptionType =
      getOptionLabel("reportOptions", selectedReportId) ===
        "Backup Power Invoice Report" ||
      getOptionLabel("reportOptions", selectedReportId) ===
        "Backup Power Abstract Report"
        ? getOptionValue("subscriptionTypes", "Backup Power")
        : getOptionValue("subscriptionTypes", "Electricity");

    return billingCycleOptions.filter(
      ({ subscriptionType }) => subscriptionType === targetSubscriptionType
    );
  }, [billingCycleOptions]);

  useEffect(() => {
    (async () => {
      if (
        getOptionLabel("reportOptions", selectedReportId) ===
          "Billing Calculation" ||
        getOptionLabel("reportOptions", selectedReportId) ===
          "Power Abstract" ||
        getOptionLabel("reportOptions", selectedReportId) ===
          "Invoice Uploader" ||
        getOptionLabel("reportOptions", selectedReportId) ===
          "Backup Power Invoice Report" ||
        getOptionLabel("reportOptions", selectedReportId) ===
          "Backup Power Abstract Report"
      ) {
        try {
          await fetchBillingOptions();
        } catch (error) {}
      }
    })();
  }, [selectedReportId]);

  useEffect(() => {
    if (updatedBillingCycleOptions?.length) {
      setBillCycleId(updatedBillingCycleOptions[0].id);
    }
  }, [updatedBillingCycleOptions]);

  useEffect(() => {
    (async () => {
      if (isConsumerWiseBillReport) {
        try {
          await getConsumerNumber(getOptionValue("utilityOptions", "DCS"));
        } catch (err) {}
      }
    })();
  }, [isConsumerWiseBillReport]);

  useEffect(() => {
    if (isServiceLine) {
      setQuery((prevState) => ({
        ...prevState,
        year: null,
      }));
    }
  }, [isServiceLine]);

  return (
    <div className="h-100">
      <Backdrop
        open={
          isLoading ||
          isFetching ||
          isExcelFileDownloading ||
          isDownloadExcelFetching ||
          isBillingCycleOptionsLoading ||
          isConsumerLoading
        }
      />

      <BreadCrumb title={t("Reports")} pageTitle={t("Dashboard")} />

      <Grid container columnSpacing={2} rowSpacing={1} mt="7px" mb="7px">
        <Grid item xs={12} sm={6} md={4} lg={3}>
          <SelectField
            onChange={(_, { value }) => {
              setSelectedReportId(value);
              setRowTableData([]);
            }}
            value={updatedReportOptions.find(
              ({ value }) => value === selectedReportId
            )}
            options={updatedReportOptions}
            renderInput={(params) => {
              delete params.inputProps.id;
              delete params.id;
              return <TextField {...params} label={t("Select Report Type")} />;
            }}
          />
        </Grid>

        {(getOptionLabel("reportOptions", selectedReportId) ===
          "Billing Calculation" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Power Abstract" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Invoice Uploader" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Backup Power Invoice Report" ||
          getOptionLabel("reportOptions", selectedReportId) ===
            "Backup Power Abstract Report") &&
          billCycleValue && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                value={{
                  label: billCycleValue.billDateDuration,
                  value: billCycleValue.id,
                }}
                getOptionLabel={({ billDateDuration }) => billDateDuration}
                getOptionValue={({ id }) => id}
                onChange={(_, { value }) => setBillCycleId(value)}
                options={updatedBillingCycleOptions}
                renderInput={(params) => {
                  delete params.inputProps.id;
                  delete params.id;
                  return (
                    <TextField {...params} label={t("Select bill cycle")} />
                  );
                }}
              />
            </Grid>
          )}
        {isConsumerVirtualAccountReportSelected && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Report Type")}
              value={reportTypeEnum.find(
                ({ value }) => query.reportType === value
              )}
              onChange={(_, { value }) =>
                setQuery((prev) => ({
                  ...prev,
                  reportType: value,
                }))
              }
              options={reportTypeEnum}
            />
          </Grid>
        )}
        {isConsumerWiseBillReport && (
          <>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Select Consumer")}
                multiple
                disableCloseOnSelect
                options={consumerNumberOptions || []}
                getOptionLabel={({ consumerNumber }) => consumerNumber}
                getOptionValue={({ id }) => id}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props} key={option.value}>
                      <Checkbox
                        sx={{ marginRight: "8px" }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                onChange={(_, options) => {
                  setConsumerPayload((prev) => ({
                    ...prev,
                    consumerIds: options,
                  }));
                }}
                value={consumerPayload.consumerIds}
                placeholder="Select Consumers"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <SelectField
                label={t("Select Years")}
                multiple
                disableCloseOnSelect
                options={generateYearOptions()}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                renderOption={(props, option, { selected }) => {
                  return (
                    <li {...props} key={option.value}>
                      <Checkbox
                        sx={{ marginRight: "8px" }}
                        checked={selected}
                      />
                      {option.label}
                    </li>
                  );
                }}
                onChange={(_, options) => {
                  setConsumerPayload((prev) => ({ ...prev, years: options }));
                }}
                value={consumerPayload.years}
                placeholder="Select Years"
              />
            </Grid>
          </>
        )}
        {isQueryRequired ? (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <DatePicker
              slotProps={{
                textField: {
                  size: "small",
                },
              }}
              label={t(isServiceLine?"Select FY Year":"Select Year")}
              className="w-100"
              views={["year"]}
              format={dateFormatter(query.year)}
              value={query.year}
              disableFuture
              onChange={(date) => {
                setQuery((prevState) => ({
                  ...prevState,
                  year: date,
                }));
              }}
            />
          </Grid>
        ) : (
          isQueryDateTime && (
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <DatePicker
                slotProps={{
                  textField: {
                    size: "small",
                  },
                }}
                label={t("Select Month and Year")}
                className="w-100"
                views={["month", "year"]}
                value={query.dateTime}
                disableFuture
                onChange={(date) => {
                  setQuery((prev) => ({
                    ...prev,
                    dateTime: date,
                  }));
                }}
              />
            </Grid>
          )
        )}

        {!!(isServiceLine&&query.year) && (
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              label={t("Select Month")}
              options={monthEnum}
              onChange={(_, options) => {
                setServiceLineMonth(options);
              }}
              value={serviceLineMonth ?? null}
              placeholder="Select Years"
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6} md={4} lg={3} sx={{ pt: 1.25, pl: 2 }}>
          <Button
            sx={{ maxHeight: "40px" }}
            variant="contained"
            color="primary"
            onClick={getReportUrl}
            disabled={
              isConsumerWiseBillReport &&
              (!consumerPayload.consumerIds.length ||
                !consumerPayload.years.length)
            }
          >
            {t("Download")}
          </Button>
        </Grid>
      </Grid>

      {!!endpoint &&
        ((!!rowTableData.length || isServerSidePaginationRequired) &&
        Component ? (
          <Component
            data={rowTableData}
            {...(componentProps || {})}
            maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
          />
        ) : (
          <BaseTable
            columns={columns}
            data={rowTableData}
            pageSize={RECORDS_PER_PAGE}
            maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 260px)"}
          />
        ))}
    </div>
  );
};

Reports.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(Reports);
